import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('vocalab_token'));
  const [loading, setLoading] = useState(true);

  // Seed school from localStorage to avoid a network round-trip on every load
  const [school, setSchool] = useState(() => {
    try {
      const cached = localStorage.getItem('vocalab_school');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });

  const _SCHOOL_CACHE_TTL = 60 * 60 * 1000; // 1 hour in ms

  const fetchSchool = async (force = false) => {
    try {
      const cachedAt = parseInt(localStorage.getItem('vocalab_school_at') || '0', 10);
      if (!force && school && Date.now() - cachedAt < _SCHOOL_CACHE_TTL) return;
      const res = await axios.get(`${API}/school`);
      setSchool(res.data);
      localStorage.setItem('vocalab_school', JSON.stringify(res.data));
      localStorage.setItem('vocalab_school_at', String(Date.now()));
    } catch (err) {
      console.error('Failed to fetch school info:', err);
    }
  };

  const fetchUser = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch (err) {
      console.error('Auth error:', err);
      logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchool();
    fetchUser();
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('vocalab_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('vocalab_token');
    localStorage.removeItem('vocalab_school');
    localStorage.removeItem('vocalab_school_at');
    setToken(null);
    setUser(null);
    setSchool(null);
  };

  const refreshSchool = () => fetchSchool(true);

  const value = {
    user,
    token,
    school,
    loading,
    login,
    logout,
    refreshSchool,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isTeacher: user?.role === 'teacher',
    isStudent: user?.role === 'student'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// API helper with auth header
export const api = {
  get: (url, config = {}) => {
    const token = localStorage.getItem('vocalab_token');
    return axios.get(`${API}${url}`, {
      ...config,
      headers: { ...config.headers, Authorization: `Bearer ${token}` }
    });
  },
  post: (url, data, config = {}) => {
    const token = localStorage.getItem('vocalab_token');
    return axios.post(`${API}${url}`, data, {
      ...config,
      headers: { ...config.headers, Authorization: `Bearer ${token}` }
    });
  },
  put: (url, data, config = {}) => {
    const token = localStorage.getItem('vocalab_token');
    return axios.put(`${API}${url}`, data, {
      ...config,
      headers: { ...config.headers, Authorization: `Bearer ${token}` }
    });
  },
  delete: (url, config = {}) => {
    const token = localStorage.getItem('vocalab_token');
    return axios.delete(`${API}${url}`, {
      ...config,
      headers: { ...config.headers, Authorization: `Bearer ${token}` }
    });
  }
};
